import * as React from 'react'
import type { ComponentProps } from 'react'

import { Box, Button, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import type SupplementValidationForm from 'modules/payroll_bundle/lib/SupplementValidationForm'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { useStatusAuthorizations } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'
import supplementValidations from 'modules/payroll_bundle/store/collections/supplementValidations'
import PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import SupplementValidationModel from 'modules/payroll_bundle/store/models/SupplementValidation'

import type SupplementLineForm from '../../index'
import TooltipContent from './TooltipContent'

type ParentFormProps = ComponentProps<typeof SupplementLineForm>

interface Props {
  employee: Employee | undefined
  store: SupplementsFormStore
  form: SupplementValidationForm
  supplementForm: SupplementForm
  onSupplementFormSubmit: ParentFormProps['onSubmit']
}

const SupplementValidationButton = ({
  form,
  store,
  employee,
  supplementForm,
  onSupplementFormSubmit,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const [disabled, setDisabled] = React.useState(false)
  const statuses: Record<string, string> = PolicyPeriod.Statuses()
  const { currentPolicyPeriods: policyPeriods } = usePolicyPeriods()
  const { access } = useCurrentSession()

  const { supplementPermissions } = useStatusAuthorizations()

  const canCreateSupplementValidations =
    supplementPermissions[String(employee?.attributes.id)]
      ?.canCreateEmployeeSupplementsValidation ?? false

  const employeeId = employee?.attributes.id
  const canSeeSupplementValidations =
    employeeId &&
    supplementPermissions[String(employeeId)]?.canReadEmployeeSupplementsValidation === true

  const currentAccessId = access.id

  const currentUserHasValidated = form.data().author_id === currentAccessId

  const validate = async () => {
    setDisabled(true)

    if (form.get('supplement_id').value && currentUserHasValidated) {
      await deleteValidation()
      setDisabled(false)

      return
    }

    // BUG: If save fails, we should not try to validate
    // If it's a draft supplement from a contract compensation, persist the supplement before validating it
    if (
      !supplementForm.get('id').value &&
      !!supplementForm.get('contracts_compensation_id').value
    ) {
      await saveSupplement()
    }

    const supplementId = Number(form.get('supplement_id').value)

    if (!supplementId) {
      form.get('supplement_id').set(supplementForm.get('id').value)
    }

    const validation = new SupplementValidationModel(form.validationData)

    const { success } = await asyncAction(() => form.save(validation))

    if (success) {
      const periodId = Number(supplementForm.get('payroll_policy_period_id').value)
      const period = policyPeriods.find((p) => p.attributes.id === periodId)
      track(trackingEvents.payroll.validateSupplement, {
        period_status: statuses[period?.attributes.status],
        supplement_id: supplementId,
      })
      fetchValidations()
    }

    setDisabled(false)
  }

  const fetchValidations = async () => await supplementValidations.fetch()

  const deleteValidation = async () => {
    const validation = new SupplementValidationModel(form.validationData)
    await validation.delete()
    store.removeSupplementValidationForm(form.validationData.id)
    fetchValidations()
  }

  const saveSupplement = async () => {
    if (!supplementForm.get('amount_in_cents').value) {
      supplementForm.get('amount_in_cents').set('0')
    }

    await onSupplementFormSubmit()
  }

  const validationSupplementId = form.data().supplement_id
  const filteredValidations = supplementValidations.filter({
    supplement_id: validationSupplementId,
  })

  if (!canCreateSupplementValidations && !canSeeSupplementValidations) {
    return null
  }

  return (
    <Box
      alignItems="flexEnd"
      aria-label={i18n.t('payroll_bundle.supplements.supplement_validation_aria_label')}
    >
      {filteredValidations.length ? (
        <Tooltip.ItemList
          attachment="bottom center"
          open={open}
          toggle={toggle}
          title={i18n.t('payroll_bundle.supplements.supplement_validations.title')}
        >
          <Button
            type={currentUserHasValidated ? 'positive' : 'secondary'}
            onClick={validate}
            disabled={disabled || !canCreateSupplementValidations}
            iconName="check"
          >
            {String(filteredValidations.length)}
          </Button>
          {filteredValidations.map((validation) => (
            <TooltipContent
              validation={validation}
              key={validation.attributes.id}
              currentAccessId={currentAccessId}
            />
          ))}
        </Tooltip.ItemList>
      ) : (
        <Button
          type={currentUserHasValidated ? 'positive' : 'secondary'}
          onClick={validate}
          disabled={disabled || !canCreateSupplementValidations}
          iconName="check"
        >
          {String(filteredValidations.length)}
        </Button>
      )}
    </Box>
  )
}

export default observer(SupplementValidationButton)
